'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/app/error-explainer-tool/components/SiteHeader';
import AppImage from '@/components/ui/AppImage';

interface RelatedBlog {
  id: string;
  title: string;
  slug: string;
  language: string;
  created_at: string;
  views: number;
}

interface Props {
  slug: string;
  title: string;
  content: string;
  language: string;
  coverImageUrl: string | null;
  views: number;
  createdAt: string;
  relatedBlogs: RelatedBlog[];
}

const LANGUAGE_COLORS: Record<string, string> = {
  javascript: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  php: 'text-violet-400 bg-violet-400/10 border-violet-400/20',
  python: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  java: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  sql: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
};

function getPopularityLabel(views: number): string | null {
  if (views >= 1000) return 'Popular';
  if (views >= 250) return 'Trending';
  if (views >= 100) return 'Rising';
  return null;
}

function isAIGeneratedLabel(line: string): boolean {
  return /\b(ai[-\s]*generated|generated by ai|written by ai)\b/i.test(line.trim());
}

function renderContent(content: string, language: string): React.ReactNode[] {
  const lines = content.split('\n');
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isAIGeneratedLabel(line)) {
      i++;
      continue;
    }

    // Code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim() || language;
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      nodes.push(
        <pre
          key={key++}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 overflow-x-auto my-4 text-xs"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          <code className={`language-${lang} text-zinc-300`}>{codeLines.join('\n')}</code>
        </pre>
      );
      i++;
      continue;
    }

    // H2
    if (line.startsWith('## ')) {
      nodes.push(
        <h2 key={key++} className="text-xl font-bold text-white mt-8 mb-3">
          {line.slice(3)}
        </h2>
      );
      i++;
      continue;
    }

    // H3
    if (line.startsWith('### ')) {
      nodes.push(
        <h3 key={key++} className="text-base font-semibold text-zinc-200 mt-5 mb-2">
          {line.slice(4)}
        </h3>
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (line.trim() === '---') {
      nodes.push(<hr key={key++} className="border-zinc-800 my-6" />);
      i++;
      continue;
    }

    // Internal link line: [text →](/errors/slug)
    const linkMatch = line.match(/^\[(.+?)\]\((.+?)\)$/);
    if (linkMatch) {
      nodes.push(
        <div key={key++} className="my-3">
          <Link
            href={linkMatch[2]}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors bg-cyan-400/5 border border-cyan-400/20 px-3 py-1.5 rounded-lg"
          >
            {linkMatch[1]}
          </Link>
        </div>
      );
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Regular paragraph
    nodes.push(
      <p key={key++} className="text-zinc-300 text-sm leading-7 mb-3">
        {line}
      </p>
    );
    i++;
  }

  return nodes;
}

export default function BlogPageClient({
  slug,
  title,
  content,
  language,
  coverImageUrl,
  views,
  createdAt,
  relatedBlogs,
}: Props) {
  useEffect(() => {
    fetch(`/api/blogs/${slug}/view`, { method: 'POST' }).catch(() => {});
  }, [slug]);

  const langColor = LANGUAGE_COLORS[language] || 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20';
  const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const popularity = getPopularityLabel(views);

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col"
      style={{ fontFamily: "'Plus Jakarta Sans', 'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #18181b; }
        ::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 3px; }
      `}</style>

      <SiteHeader />

      <div className="border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link
            href="/blog"
            className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors text-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
          <div className="flex items-center gap-2 text-zinc-500 text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {popularity ? (
              <span className="inline-flex items-center gap-1.5 text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-300" />
                {popularity}
              </span>
            ) : (
              <span className="text-zinc-500">Developer guide</span>
            )}
          </div>
        </div>
      </div>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-6" aria-label="Breadcrumb">
          <Link href="/error-explainer-tool" className="hover:text-zinc-300 transition-colors">Home</Link>
          <span>/</span>
          <Link href="/blog" className="hover:text-zinc-300 transition-colors">Blog</Link>
          <span>/</span>
          <Link href={`/languages/${language}`} className="hover:text-zinc-300 transition-colors capitalize">{language}</Link>
          <span>/</span>
          <span className="text-zinc-400 truncate max-w-xs">{title.slice(0, 50)}</span>
        </nav>

        {/* Article Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${langColor}`}>
              {language.toUpperCase()}
            </span>
            <span className="text-xs text-zinc-600">{formattedDate}</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 leading-tight">{title}</h1>

          {coverImageUrl && (
            <div className="rounded-2xl overflow-hidden border border-zinc-800 mb-5">
              <AppImage
                src={coverImageUrl}
                alt={title}
                width={1200}
                height={675}
                className="w-full h-auto object-cover"
                priority
              />
            </div>
          )}
        </div>

        {/* Article Content */}
        <article className="prose-custom">
          {renderContent(content, language)}
        </article>

        {/* Language back-link */}
        <div className="mt-10 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 flex items-center gap-2.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-500 flex-shrink-0">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <span className="text-zinc-500 text-xs">Browse all</span>
          <Link
            href={`/languages/${language}`}
            className="text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1"
          >
            {language.charAt(0).toUpperCase() + language.slice(1)} errors
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Related Blog Articles */}
        {relatedBlogs.length > 0 && (
          <section className="mt-10">
            <h2 className="text-base font-semibold text-zinc-100 mb-4 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-cyan-400">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              Related {language.toUpperCase()} Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {relatedBlogs.map((rel) => (
                <Link
                  key={rel.id}
                  href={`/blog/${rel.slug}`}
                  className="group bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-600 hover:bg-zinc-800/50 transition-all"
                >
                  <p className="text-zinc-300 text-xs font-medium group-hover:text-white transition-colors mb-2 line-clamp-2 leading-5">
                    {rel.title}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${LANGUAGE_COLORS[rel.language] || 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20'}`}>
                      {rel.language.toUpperCase()}
                    </span>
                    {getPopularityLabel(rel.views) ? (
                      <span className="text-[10px] uppercase tracking-wide text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2 py-0.5">
                        {getPopularityLabel(rel.views)}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-600">Guide</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-10 bg-zinc-900 border border-zinc-800 rounded-2xl p-6 text-center">
          <p className="text-zinc-400 text-sm mb-3">Have a specific error? Get an instant explanation.</p>
          <Link
            href="/error-explainer-tool"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500 text-zinc-950 font-semibold text-sm hover:bg-cyan-400 transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Explain an Error
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6 mt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-zinc-600 text-xs">
          <p>DevError Explainer — A self-growing developer knowledge base</p>
        </div>
      </footer>
    </div>
  );
}
